import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import * as currency from 'currency-formatter';
import { Mail, MessageCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { type Control, type FieldValues, useWatch } from 'react-hook-form';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { CopyText } from '@/components/ui/copy-text';
import { SortableHeader } from '@/components/ui/data-table';
import { Field, FieldLabel } from '@/components/ui/field';
import { FormAsyncSelect } from '@/components/ui/form-fields/form-async-select';
import { FormCurrencyInput } from '@/components/ui/form-fields/form-currency-input';
import { FormInput, lgSpan } from '@/components/ui/form-fields/form-input';
import { FormMaskedInput } from '@/components/ui/form-fields/form-mask-input';
import { FormSelect } from '@/components/ui/form-fields/form-select';
import { FormSwitch } from '@/components/ui/form-fields/form-switch';
import { FormUploader } from '@/components/ui/form-fields/form-uploader';
import { MultiSelect } from '@/components/ui/multi-select';
import type { Filter } from '@/components/ui/search-toolbar';
import type {
  FieldCondition,
  FieldDef,
  FilterDef,
  RelationRef,
  ColumnDef as ScreenColumnDef,
} from '@/db/schema';
import { apiClient } from '@/lib/api-client';
import { date, localeFormat } from '@/lib/date';
import { applyBestMask } from '@/lib/mask';
import { cn } from '@/lib/utils';
import { cellComponentRegistry, fieldComponentRegistry } from './component-registry';

/** Ordena por `order` (menor primeiro); empate mantém a ordem original do array (sort estável). */
function sortByOrder<T extends { order?: number }>(items: T[]): T[] {
  return [...items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

/**
 * Hook de busca (com search-as-you-type) pra um recurso qualquer, no shape que
 * `AsyncSelect`/`FormAsyncSelect`/`SearchToolbar` esperam em `useQuery`/`selectOptions.useQuery`.
 */
function makeRelationSearchHook({ resource, orderBy, order }: RelationRef) {
  return (params?: { search?: string }) =>
    useQuery({
      queryKey: ['relation-search', resource, orderBy, order, params?.search],
      queryFn: async () => {
        const res = await (apiClient as any)[resource].get({
          query: { search: params?.search, perPage: 50, orderBy, order },
        });
        return (res.data?.data ?? []) as any[];
      },
    });
}

/** Lê a relação N:N de um registro via a rota aninhada `GET /:resource/:id/:relatedResource`. */
function useNestedRelation(
  resource: string,
  recordId: string | undefined,
  relatedResource: string,
) {
  return useQuery({
    queryKey: ['nested-relation', resource, recordId, relatedResource],
    queryFn: async () => {
      const res = await (apiClient as any)
        [resource]({ id: recordId })
        [relatedResource].get();
      return (res.data ?? []) as any[];
    },
    enabled: !!recordId,
  });
}

/** Só dígitos, com DDI 55 na frente se faltar (assume número brasileiro). */
function toWhatsAppUrl(phone: string) {
  const digits = phone.replace(/\D/g, '');
  const withCountry = digits.startsWith('55') ? digits : `55${digits}`;
  return `https://wa.me/${withCountry}`;
}

/** Lê 'a.b.c' de um objeto (colunas de relação carregadas pelo repository, ex: 'event.name'). */
function getPath(obj: any, path: string) {
  return path.split('.').reduce((acc, key) => acc?.[key], obj);
}

export function layoutToColumns(
  columns: ScreenColumnDef[],
  resource: string,
): ColumnDef<any>[] {
  return sortByOrder(columns).map((col) => {
    // Caminho aninhado não existe como coluna no backend — `orderBy` seria rejeitado.
    const sortable = col.sortable !== false && !col.name.includes('.');

    return {
      id: col.name,
      accessorFn: (row: any) => getPath(row, col.name),
      enableSorting: sortable,
      meta: { label: col.label, type: col.type },
      header: ({ column }: any) =>
        !sortable ? col.label : <SortableHeader column={column} label={col.label} />,
      cell: ({ row }: any) => {
        if (col.type === 'multi-relation' && col.relation?.resource) {
          return (
            <RelationBadges
              resource={resource}
              recordId={row.original.id}
              relation={col.relation}
            />
          );
        }

        const value = row.getValue(col.name);

        if (col.type === 'component' && col.component) {
          const Component = cellComponentRegistry[col.component];
          return Component ? <Component value={value} row={row.original} /> : value;
        }

        if (col.type === 'avatar') {
          const name = (row.original?.name as string | undefined) ?? '';
          const initials =
            name
              .split(' ')
              .map((part) => part[0])
              .slice(0, 2)
              .join('')
              .toUpperCase() || '?';

          return (
            <Avatar className="size-9">
              {value && <AvatarImage src={value} alt={name} />}
              <AvatarFallback className="text-[11px]">{initials}</AvatarFallback>
            </Avatar>
          );
        }

        if (col.type === 'email') {
          if (!value) return null;
          return (
            <a
              href={`mailto:${value}`}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1.5 text-primary hover:underline"
            >
              <Mail className="size-3.5 shrink-0" />
              {value}
            </a>
          );
        }

        if (col.type === 'phone') {
          if (!value) return null;
          return (
            <a
              href={toWhatsAppUrl(String(value))}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1.5 text-olive-ink hover:underline"
            >
              <MessageCircle className="size-3.5 shrink-0" />
              {value}
            </a>
          );
        }

        if (col.name === 'id' && typeof value === 'string') {
          return <CopyText value={value} label={`${value.slice(0, 8)}...`} />;
        }

        if (col.mask && col.mask.length > 0) {
          return value ? applyBestMask(String(value), col.mask) : value;
        }

        if (col.type === 'boolean') {
          const label = col.options?.find(
            (option) => option.value === String(!!value),
          )?.label;
          return (
            <Badge variant={value ? 'success' : 'destructive'}>
              {label ?? (value ? 'Sim' : 'Não')}
            </Badge>
          );
        }

        if (col.type === 'enum' && col.options) {
          return col.options.find((option) => option.value === value)?.label ?? value;
        }

        if (col.type === 'date' || col.type === 'datetime') {
          return value ? date(value).format(localeFormat) : '';
        }

        if (col.type === 'currency') {
          return currency.format(value ?? 0, { code: 'BRL' });
        }

        return value;
      },
    };
  });
}

function RelationBadges({
  resource,
  recordId,
  relation,
}: {
  resource: string;
  recordId: string;
  relation: RelationRef;
}) {
  const { data, isLoading } = useNestedRelation(resource, recordId, relation.resource);

  if (isLoading) return <span className="text-muted-foreground text-xs">...</span>;
  if (!data?.length) return <span className="text-muted-foreground text-xs">—</span>;

  return (
    <div className="flex flex-wrap gap-1">
      {data.map((item) => (
        <Badge key={item.id} variant="secondary">
          {item[relation.displayField]}
        </Badge>
      ))}
    </div>
  );
}

export function layoutToFilters(filters: FilterDef[]): Filter<any>[] {
  return sortByOrder(filters).map((f) => {
    if (f.relation?.resource) {
      const relation = f.relation;

      return {
        type: f.type,
        label: f.label,
        name: f.name,
        selectOptions: {
          useQuery: makeRelationSearchHook(relation),
          getOptionValue: (item: any) => item.id,
          getDisplayValue: (item: any) => item[relation.displayField],
        },
      };
    }

    return {
      type: f.type,
      label: f.label,
      name: f.name,
      options: f.options,
    };
  });
}

/**
 * Resolve o `defaultValue` dos filtros da screen pra valores do SearchToolbar.
 * `{ nextBy }` busca na API do recurso relacionado o próximo registro a partir de hoje
 * (fallback: o mais recente) — por isso é assíncrono.
 */
export function useFilterDefaults(filters: FilterDef[] | undefined) {
  const withDefault = (filters ?? []).filter((f) => f.defaultValue !== undefined);

  return useQuery({
    queryKey: ['filter-defaults', withDefault],
    enabled: !!filters,
    queryFn: async () => {
      const values: Record<string, string> = {};
      const labels: Record<string, string> = {};

      for (const f of withDefault) {
        const def = f.defaultValue;

        if (typeof def === 'string') {
          values[f.name] = def;
          continue;
        }

        if (!def || !f.relation?.resource) continue;

        const api = (apiClient as any)[f.relation.resource];
        const next = await api.get({
          query: {
            [`${def.nextBy}From`]: date().format('YYYY-MM-DD'),
            orderBy: def.nextBy,
            order: 'asc',
            perPage: 1,
          },
        });
        let item = next.data?.data?.[0];

        if (!item) {
          const last = await api.get({
            query: { orderBy: def.nextBy, order: 'desc', perPage: 1 },
          });
          item = last.data?.data?.[0];
        }

        if (item) {
          values[f.name] = item.id;
          labels[f.name] = item[f.relation.displayField];
        }
      }

      return { values, labels };
    },
  });
}

interface ScreenFormProps {
  fields: FieldDef[];
  control: Control<FieldValues>;
  mode: 'create' | 'edit' | 'view';
  /** Necessários pra campos `type: 'multi-relation'` (leem/gravam via rota aninhada) e `component`. */
  resource?: string;
  recordId?: string;
}

/**
 * Renderiza os campos de uma screen a partir do layout salvo no banco.
 * Estado "view" (somente leitura) é resolvido pelo form-level `useForm({ disabled: true })`
 * do consumidor, não aqui — por campo só `disabledOnEdit` e `enabledIf` desabilitam, e
 * `visibleIf` esconde (ver `evaluateCondition`).
 */
/** Clampa pra 1-12 e cai num inteiro — evita classe inválida se vier lixo do banco. */
function clampWidth(width: number | undefined, fallback: number): keyof typeof lgSpan {
  const n = Math.round(width ?? fallback);
  return Math.min(
    12,
    Math.max(1, Number.isFinite(n) ? n : fallback),
  ) as keyof typeof lgSpan;
}

/**
 * Valores iniciais do formulário de criação: boolean começa `false` (como o `defaultChecked`
 * do antigo) — sem isso, switch escondido no create (ex: "Participou do Mód. 2?", só na edição)
 * vai `undefined` e a API rejeita.
 */
export function fieldDefaults(fields: FieldDef[]) {
  return Object.fromEntries(
    fields
      .filter((f) => f.defaultValue !== undefined || f.type === 'boolean')
      .map((f) => [f.name, f.defaultValue ?? false]),
  );
}

function isEmpty(value: unknown) {
  return value === undefined || value === null || value === '';
}

/** Mesma semântica do `evaluateCondition` do admin antigo (app/src/components/ui/form.tsx). */
export function evaluateCondition(
  condition: FieldCondition,
  values: Record<string, any>,
): boolean {
  const value = values[condition.field];
  const list = (condition.value ?? '').split(',').map((v) => v.trim());

  switch (condition.operator) {
    case 'equals':
      return String(value ?? '') === (condition.value ?? '');
    case 'notEquals':
      return String(value ?? '') !== (condition.value ?? '');
    case 'in':
      return list.includes(String(value ?? ''));
    case 'notIn':
      return !list.includes(String(value ?? ''));
    case 'exists':
      return typeof value === 'boolean' ? value : !isEmpty(value);
    case 'notExists':
      return typeof value === 'boolean' ? !value : isEmpty(value);
    default:
      return true;
  }
}

export function evaluateConditions(
  conditions: FieldCondition[] | undefined,
  values: Record<string, any>,
) {
  return (conditions ?? []).every((c) => evaluateCondition(c, values));
}

export function ScreenForm({
  fields,
  control,
  mode,
  resource,
  recordId,
}: ScreenFormProps) {
  const watched = useWatch({ control });
  // `id` entra nas condições pra permitir "só na edição" (id exists) / "só na criação" (notExists).
  const values = { ...watched, id: recordId };

  return (
    <div className="grid grid-cols-12 gap-4 col-span-full">
      {sortByOrder(fields)
        .filter((field) => {
          if (!evaluateConditions(field.visibleIf, values)) return false;
          if (mode === 'create') return field.showOnCreate !== false;
          if (mode === 'edit') return field.showOnEdit !== false;
          return field.showOnView !== false;
        })
        .map((field) => {
          const disabled =
            (mode === 'edit' && field.disabledOnEdit) ||
            !evaluateConditions(field.enabledIf, values);
          const isMultiRelation = field.type === 'multi-relation';
          const width = clampWidth(field.width, isMultiRelation ? 12 : 4);

          const base = {
            name: field.name,
            control,
            label: field.label,
            required: field.required,
          };

          let content: ReactNode = null;

          if (isMultiRelation && field.relation?.resource) {
            content =
              !resource || !recordId ? (
                <Field className="col-span-full">
                  <FieldLabel>{field.label}</FieldLabel>
                  <p className="text-sm text-muted-foreground">
                    Salve o registro primeiro pra configurar esta relação.
                  </p>
                </Field>
              ) : (
                <MultiRelationField
                  resource={resource}
                  recordId={recordId}
                  label={field.label}
                  relation={field.relation}
                  readOnly={mode === 'view'}
                />
              );
          } else if (field.type === 'component' && field.component) {
            const Component = fieldComponentRegistry[field.component];
            content = Component ? (
              <Component {...base} disabled={disabled} mode={mode} recordId={recordId} />
            ) : null;
          } else if (field.mask && field.mask.length > 0) {
            content = <FormMaskedInput {...base} mask={field.mask} disabled={disabled} />;
          } else if (field.type === 'boolean') {
            content = <FormSwitch {...base} disabled={disabled} />;
          } else if (field.type === 'enum') {
            content = (
              <FormSelect {...base} options={field.options ?? []} disabled={disabled} />
            );
          } else if (field.type === 'relation' && field.relation?.resource) {
            content = (
              <RelationField {...base} relation={field.relation} disabled={disabled} />
            );
          } else if (field.type === 'currency') {
            content = <FormCurrencyInput {...base} disabled={disabled} />;
          } else if (field.type === 'date') {
            content = <FormInput {...base} type="date" disabled={disabled} />;
          } else if (field.type === 'datetime') {
            content = <FormInput {...base} type="datetime-local" disabled={disabled} />;
          } else if (field.type === 'number') {
            content = <FormInput {...base} type="number" disabled={disabled} />;
          } else if (field.type === 'password') {
            content = <FormInput {...base} type="password" disabled={disabled} />;
          } else if (field.type === 'file') {
            content = (
              <FormUploader
                {...base}
                accept={field.accept}
                avatar={field.avatarPreview}
                disabled={disabled}
              />
            );
          } else {
            content = <FormInput {...base} disabled={disabled} />;
          }

          if (!content) return null;

          return (
            <div key={field.name} className={cn('col-span-12', lgSpan[width])}>
              {content}
            </div>
          );
        })}
    </div>
  );
}

function RelationField({
  relation,
  ...props
}: {
  name: string;
  control: Control<FieldValues>;
  label: string;
  required?: boolean;
  disabled?: boolean;
  sm?: any;
  md?: any;
  lg?: any;
  relation: RelationRef;
}) {
  return (
    <FormAsyncSelect
      {...props}
      useQuery={makeRelationSearchHook(relation)}
      getOptionValue={(item: any) => item.id}
      getDisplayValue={(item: any) => item[relation.displayField]}
      clearable
    />
  );
}

/**
 * Campo de relação N:N — não passa pelo react-hook-form (a associação não é um campo do
 * recurso, é uma rota aninhada à parte). Cada mudança já salva na hora via PUT.
 */
function MultiRelationField({
  resource,
  recordId,
  label,
  relation,
  readOnly,
}: {
  resource: string;
  recordId: string;
  label: string;
  relation: RelationRef;
  readOnly?: boolean;
}) {
  const queryClient = useQueryClient();
  const queryKey = ['nested-relation', resource, recordId, relation.resource];

  const { data: current } = useNestedRelation(resource, recordId, relation.resource);
  const { data: options } = makeRelationSearchHook(relation)();

  const mutation = useMutation({
    mutationFn: async (ids: string[]) => {
      await (apiClient as any)
        [resource]({ id: recordId })
        [relation.resource].put({ ids });
    },
    onMutate: async (ids: string[]) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<any[]>(queryKey);
      const nextItems = (options ?? []).filter((item: any) => ids.includes(item.id));
      queryClient.setQueryData(queryKey, nextItems);
      return { previous };
    },
    onError: (_err, _ids, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(queryKey, ctx.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });

  const selectedIds = (current ?? []).map((item) => item.id);

  return (
    <Field className="col-span-full">
      <FieldLabel>{label}</FieldLabel>
      <MultiSelect
        label={label}
        options={options ?? []}
        getOptionValue={(item: any) => item.id}
        getDisplayValue={(item: any) => item[relation.displayField]}
        value={selectedIds}
        onChange={(ids) => mutation.mutate(ids)}
        disabled={readOnly}
      />
    </Field>
  );
}
